import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { UIMessage } from "ai";
import { ArrowLeft } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { AssistantChat } from "@/components/assistant/AssistantChat";
import { UsageWarning } from "@/components/assistant/UsageWarning";
import { FeatureGate } from "@/components/UpgradeGate";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/assistant/$threadId")({
  head: () => ({
    meta: [
      { title: "Conversation — MyPriorities Assistant" },
      {
        name: "description",
        content:
          "Chat with your hub assistant about capacity, goals, commitments and habits, and approve suggested changes.",
      },
      { property: "og:title", content: "Conversation — MyPriorities Assistant" },
      {
        property: "og:description",
        content: "Ask about your week and approve the changes the assistant suggests.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AssistantThreadRoute,
});

function AssistantThreadRoute() {
  return (
    <FeatureGate requires="assistant" title="Conversation" subtitle="Available on Pro and Premium.">
      <AssistantThread />
    </FeatureGate>
  );
}

function AssistantThread() {
  const { threadId } = Route.useParams();

  const thread = useQuery({
    queryKey: ["chat_thread", threadId],
    queryFn: async () => {
      const [threadRes, messagesRes] = await Promise.all([
        supabase.from("chat_threads").select("id, title").eq("id", threadId).maybeSingle(),
        supabase
          .from("chat_messages")
          .select("message, created_at")
          .eq("thread_id", threadId)
          .order("created_at", { ascending: true }),
      ]);
      if (threadRes.error) throw threadRes.error;
      if (messagesRes.error) throw messagesRes.error;
      return {
        title: threadRes.data?.title ?? "Conversation",
        messages: (messagesRes.data ?? []).map((row) => row.message as unknown as UIMessage),
      };
    },
  });

  return (
    <AppShell
      title={thread.data?.title ?? "Conversation"}
      subtitle="Grounded in your live hub data. Changes need your approval."
      action={
        <Button variant="outline" size="sm" asChild>
          <Link to="/assistant">
            <ArrowLeft className="size-4" /> All conversations
          </Link>
        </Button>
      }
    >
      <UsageWarning />
      {thread.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading conversation…</p>
      ) : (
        <AssistantChat threadId={threadId} initialMessages={thread.data?.messages ?? []} />
      )}
    </AppShell>
  );
}
